import { apiService } from '@paysense-india-services/payufin-jsutils'

import { getApiUrl } from '@/config/products'

import { getAdapter } from './adapters'
import { OPERATION_ENDPOINT_KEY } from './operations'

const buildPlatformHeaders = config => ({
  ...(config?.platformKey ? { 'X-PLATFORM-KEY': config.platformKey } : {})
})

const requestConfig = (config, extraHeaders) => ({
  credentials: 'include',
  headers: {
    ...buildPlatformHeaders(config),
    ...extraHeaders
  }
})

const enrichInput = (config, input) => ({
  ...input,
  ...(config.emailOtpSource ? { emailOtpSource: config.emailOtpSource } : {})
})

const resolveEndpointKey = (adapter, operation, input) =>
  adapter.getEndpointKey?.(operation, input) ?? OPERATION_ENDPOINT_KEY[operation]

const resolveHttpMethod = (adapter, operation, input) =>
  adapter.getHttpMethod?.(operation, input) ?? adapter.getHttpMethod?.(operation) ?? 'POST'

/**
 * Resolve URL, build wire request, call API, return normalized response.
 * @param {object} config - ProductConfig
 * @param {string} operation - PRODUCT_OPERATIONS value
 * @param {object} [input]
 */
export const callProductApi = async (config, operation, input = {}) => {
  const adapter = getAdapter(config.source)
  const enrichedInput = enrichInput(config, input)
  const endpointKey = resolveEndpointKey(adapter, operation, enrichedInput)
  const url = getApiUrl(config, endpointKey)

  if (!url) {
    throw new Error(`Endpoint not configured for ${operation} (${config.source})`)
  }

  const data = adapter.buildRequest(operation, enrichedInput)
  const extraHeaders = adapter.buildHeaders(config, operation, enrichedInput)
  const method = resolveHttpMethod(adapter, operation, enrichedInput)

  const raw =
    method === 'GET'
      ? await apiService.get(url, {
          ...requestConfig(config, extraHeaders),
          params: data
        })
      : await apiService.post(url, {
          ...requestConfig(config, extraHeaders),
          data
        })

  return adapter.normalizeResponse(operation, raw)
}

/**
 * Build auth-vista / OTPForm request shape without calling the API.
 * @param {object} config
 * @param {string} operation
 * @param {object} [input]
 */
export const getProductRequest = (config, operation, input = {}) => {
  const adapter = getAdapter(config.source)
  const enrichedInput = enrichInput(config, input)
  const endpointKey = resolveEndpointKey(adapter, operation, enrichedInput)
  const extraHeaders = adapter.buildHeaders(config, operation, enrichedInput)

  return {
    url: getApiUrl(config, endpointKey),
    data: adapter.buildRequest(operation, enrichedInput),
    headers: {
      ...buildPlatformHeaders(config),
      ...extraHeaders
    },
    credentials: 'include'
  }
}

/**
 * Normalize a raw auth-vista response (e.g. verify login onSuccess).
 */
export const normalizeProductResponse = (config, operation, raw) =>
  getAdapter(config.source).normalizeResponse(operation, raw)
